/**
 * Универсальный http-клиент поверх axios.
 *
 * Что делает:
 *   • baseURL: `${VITE_API_BASE_URL}/v1` (флаг withoutVersion выходит за
 *     этот префикс — например для `/storage/...`). Платформенные пути
 *     идут как `admin/...`, клиентские — как `auth/...`, `billing/...`
 *     (см. ADMIN_BACKEND_SPEC §3).
 *   • Bearer-токен из localStorage. Контур выбирается опцией `contour`
 *     ('team' по умолчанию — платформенная зона; 'client' — кабинет).
 *     Ключи те же, что пишет `atomWithStorage` в `atoms/auth/` — поэтому
 *     axios-слой и атомы автоматически синхронизированы.
 *   • Country-Code header из env.
 *   • snake_case ↔ camelCase конверсия тел и query-параметров.
 *   • Распаковка legacy-конверта `{ result, status, payload }` — при
 *     `result !== 0` кидаем `HttpError`, в success возвращаем `payload`.
 *   • 401 команды → один общий refresh (POST auth/refresh) и повтор
 *     запроса. Refresh отвергнут — `HttpError` 401, выход делает слой
 *     выше (QueryClient global onError).
 *
 * ВАЖНО (v0.1): весь data-слой сейчас на моках (services/<domain>/mocks.ts),
 * этот клиент ещё не вызывается из доменов. Он готов к подключению BE —
 * фетчер меняет `MOCK_*` на `http(...)`, остальное не трогается.
 */

import axios, {type AxiosError, type AxiosInstance, type AxiosRequestConfig} from 'axios';

import type {AuthContour} from './auth-storage';
import {
  CLIENT_TOKEN_STORAGE_KEY,
  readTeamRefresh,
  TEAM_TOKEN_STORAGE_KEY,
  writeTeamRefresh,
} from './auth-storage';
import {toCamel, toSnake} from './case-convert';
import {env} from './env';
import {HttpError, type HttpOptions} from './http.types';

/* ── Axios instance ───────────────────────────────────────────── */

/** Строим базовый URL. Относительный (/api) остаётся относительным. */
const baseUrl = (withoutVersion: boolean): string => {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '');
  const version = withoutVersion ? '' : '/v1';
  return base + version;
};

const client: AxiosInstance = axios.create({
  baseURL: baseUrl(false),
  withCredentials: false,
  headers: {Accept: '*/*'},
  // FastAPI ждёт повторяющиеся параметры-массивы: cities=a&cities=b (без скобок)
  paramsSerializer: {indexes: null},
});

/* ── Interceptors ─────────────────────────────────────────────── */

/**
 * Читаем токен из localStorage. `atomWithStorage` пишет через
 * `JSON.stringify`, поэтому строка хранится в кавычках — нужно
 * парсить обратно перед использованием в Bearer-заголовке. Иначе
 * BE получает `Bearer "abc"` и кидает `Invalid crypto padding`.
 */
const readToken = (contour: AuthContour): string | null => {
  const key = contour === 'client' ? CLIENT_TOKEN_STORAGE_KEY : TEAM_TOKEN_STORAGE_KEY;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    return raw;
  }
};

client.interceptors.request.use((config) => {
  config.headers.set('Country-Code', env.VITE_COUNTRY_CODE);

  const cfgX = config as AxiosRequestConfig & {
    contour?: AuthContour;
    withoutVersion?: boolean;
    skipCaseConversion?: boolean;
  };

  // Токен — напрямую из localStorage. atomWithStorage пишет туда же,
  // так что atoms/auth и http смотрят на один источник.
  const token = readToken(cfgX.contour ?? 'team');
  if (token) config.headers.set('Authorization', `Bearer ${token}`);

  // Snake-case body+params (если явно не отключено через skipCaseConversion).
  if (cfgX.skipCaseConversion !== true) {
    if (config.data && !(config.data instanceof FormData)) {
      config.data = toSnake(config.data);
    }
    if (config.params && typeof config.params === 'object') {
      config.params = toSnake(config.params);
    }
  }

  if (cfgX.withoutVersion) {
    config.baseURL = baseUrl(true);
  }

  return config;
});

/* ── Refresh команды ──────────────────────────────────────────── */

/** Ручки входа не продлеваем: их 401 — ответ по существу, а не протухший токен */
const AUTH_PATHS = /^\/?auth\/(login|refresh|logout|change-password|password\/)/;

/** Single-flight: параллельные 401 ждут один общий refresh */
let refreshInFlight: Promise<string | null> | null = null;

const refreshTeamAccess = (): Promise<string | null> => {
  refreshInFlight ??= (async () => {
    const refreshToken = readTeamRefresh();
    if (refreshToken === null) return null;
    try {
      const res = await axios.post<{accessToken?: string}>(
        `${baseUrl(false)}/auth/refresh`,
        {refreshToken},
        {headers: {'Country-Code': env.VITE_COUNTRY_CODE}},
      );
      const token = res.data.accessToken;
      if (typeof token !== 'string' || token === '') return null;
      // Формат atomWithStorage — JSON-строка: атом и http читают одно и то же
      localStorage.setItem(TEAM_TOKEN_STORAGE_KEY, JSON.stringify(token));
      return token;
    } catch (e) {
      // Refresh отозван или протух — дальше только вход. Сеть/5xx — не трогаем
      const status = (e as AxiosError).response?.status ?? 0;
      if (status >= 400 && status < 500) writeTeamRefresh(null);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

type RetryConfig = AxiosRequestConfig & {contour?: AuthContour; retried?: boolean};

client.interceptors.response.use(
  (res) => {
    const skip = (res.config as {skipCaseConversion?: boolean}).skipCaseConversion === true;
    res.data = skip ? res.data : toCamel(res.data);
    return res;
  },
  async (err: AxiosError) => {
    const status = err.response?.status ?? 0;
    const cfg = err.config as RetryConfig | undefined;
    if (
      status === 401 &&
      cfg !== undefined &&
      cfg.retried !== true &&
      (cfg.contour ?? 'team') === 'team' &&
      !AUTH_PATHS.test(cfg.url ?? '')
    ) {
      const fresh = await refreshTeamAccess();
      // Повтор: request-интерсептор заново возьмёт токен из localStorage
      if (fresh !== null) return client.request({...cfg, retried: true} as RetryConfig);
    }
    const body = err.response?.data;
    return Promise.reject(
      new HttpError(err.response?.statusText || err.message || 'HTTP error', status, body),
    );
  },
);

/* ── Public API ───────────────────────────────────────────────── */

/**
 * Основной fetch-хелпер. Все сервисы зовут только его.
 *
 *   const clients = await http<Client[]>('admin/clients');
 *   await http('admin/clients', {method: 'POST', body: {…}});
 *   const me = await http('auth/me', {contour: 'client'});
 */
export const http = async <T = unknown>(path: string, options: HttpOptions = {}): Promise<T> => {
  const {body, params, contour, withoutVersion, skipCaseConversion, rawResponse, ...rest} = options;

  // Чистим undefined/null params, чтобы axios их не сериализовал.
  const cleanParams = params
    ? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null))
    : undefined;

  const res = await client.request<unknown>({
    url: path.startsWith('/') ? path.slice(1) : path,
    method: rest.method ?? (body !== undefined ? 'POST' : 'GET'),
    data: body,
    params: cleanParams,
    ...rest,
    ...(contour ? {contour} : {}),
    ...(withoutVersion ? {withoutVersion: true} : {}),
    ...(skipCaseConversion ? {skipCaseConversion: true} : {}),
  } as AxiosRequestConfig);

  const data = res.data;

  if (rawResponse) return data as T;

  // Legacy envelope: { result, status, payload }.
  if (data && typeof data === 'object') {
    const envelope = data as {result?: number; status?: string; payload?: unknown};
    if (envelope.result !== undefined && envelope.result !== 0) {
      throw new HttpError(envelope.status ?? 'api_error', res.status, envelope);
    }
    if ('payload' in envelope) return envelope.payload as T;
  }

  return data as T;
};
